import { useMutation, useQueries } from "@tanstack/react-query";
import React from "react";
import { API } from "../../API";
import useInstaPay from "../../hooks/useInstaPay";
import {
  CaregiverSetBankInformation,
  InstaPayBankAccountTypeId,
  InstaPayBankId,
  VisitInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { HashSet } from "../../utils/HashSet";
import { useModalV2 } from "../AppModals";
import ErrorScaffold from "../ErrorScaffold";
import LoadingScaffold from "../LoadingScaffold";
import FillBankInformationScreen from "./FillBankInformationScreen";

type Props = {
  api: API;
  visitInstanceIds: VisitInstanceId[];
  onRequestGoToMain: () => void;
  onClickGoBack: () => void;
};

const FillBankInformationActivity = (props: Props) => {
  const modals = useModalV2();
  const instapay = useInstaPay({
    api: props.api,
    onRequestGoToMain: props.onRequestGoToMain,
    onRequestNavigate: () => {},
  });

  const [banksQuery, accountTypesQuery, overviewQuery] = useQueries({
    queries: [
      { queryKey: ["banks"], queryFn: () => props.api.getBanks() },
      { queryKey: ["accountTypes"], queryFn: () => props.api.getBankAccountTypes() },
      {
        queryKey: ["instapay", "history", 0],
        queryFn: () => props.api.getPayrollInformation(0),
      },
    ],
  });

  const setBankInformation = useMutation({
    mutationFn: (body: CaregiverSetBankInformation) => props.api.setBankInformation(body),
  });

  const handleValidSubmit = (body: {
    bankId: string;
    accountType: string;
    routingNumber: string;
    accountNumber: string;
    email: string;
  }) => {
    const modal = modals.createModal({ type: "neutral", isLoading: true, title: "" });

    setBankInformation.mutate(
      {
        bankId: InstaPayBankId.wrap(parseInt(body.bankId, 10)),
        accountType: InstaPayBankAccountTypeId.wrap(parseInt(body.accountType, 10)),
        routingNumber: body.routingNumber,
        accountNumber: body.accountNumber,
        email: body.email,
      },
      {
        onSuccess: () => {
          instapay.withdraw({
            modalInstance: modal,
            bankInformation: { hasAgreedToTerms: true },
            visits: HashSet.fromCollection(props.visitInstanceIds.map((id) => ({ id }))),
          });
        },
        onError: () =>
          modal.update({
            type: "error",
            isLoading: false,
            title: translate(STRINGS.ERROR),
            message: translate(STRINGS.SOMETHING_WENT_WRONG),
          }),
      }
    );
  };

  if (banksQuery.isLoading || accountTypesQuery.isLoading || overviewQuery.isLoading) {
    return (
      <LoadingScaffold title={translate(STRINGS.INSTAPAY)} onClickGoBack={props.onClickGoBack} />
    );
  }

  if (banksQuery.isError || accountTypesQuery.isError || overviewQuery.isError) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.OOPS)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  return (
    <FillBankInformationScreen
      bankAccountNames={banksQuery.data.banks}
      bankAccountTypes={accountTypesQuery.data.accountTypes}
      defaultValues={overviewQuery.data.bankInformation}
      title={translate(STRINGS.ONE_LAST_STEP)}
      onValidSubmit={handleValidSubmit}
      onClickGoBack={props.onClickGoBack}
    />
  );
};

export default FillBankInformationActivity;
